import Layout from "@/components/Layout"
import { Link } from "react-router-dom"
import {Calculator, TrendingDown, Building, Laptop, Truck, Home as HomeIcon, ArrowRight, } from 'lucide-react'

function Home() {
  const featuredTools = [
    {
      title: "Straight Line Method Amortization Calculator",
      description:
        "Calculate depreciation using the straight line method with salvage value and useful life.",
      icon: Calculator,
      href: "/calculator/straight-line",
      color: "bg-blue-500",
    },
    {
      title: "Double Declining Balance Calculator",
      description:
        "Accelerated depreciation method for faster asset value reduction in early years.",
      icon: TrendingDown,
      href: "/calculator/double-declining",
      color: "bg-indigo-500",
    },
    {
      title: "MACRS Tax Depreciation Calculator",
      description:
        "IRS-approved Modified Accelerated Cost Recovery System for tax deductions.",
      icon: Building,
      href: "/calculator/macrs",
      color: "bg-blue-600",
    },
    {
      title: "Rental Property & Real Estate Depreciation",
      description:
        "Specialized calculator for rental properties and commercial real estate assets.",
      icon: HomeIcon,
      href: "/calculator/rental-property",
      color: "bg-cyan-500",
    },
  ]

  const assetTools = [
    {
      category: "Computer & Hardware",
      description: "Depreciation calculators for IT equipment and hardware life",
      icon: Laptop,
      href: "/calculator/computer-hardware",
    },
    {
      category: "Furniture & Appliances",
      description: "Calculate depreciation for office furniture and appliances",
      icon: Building,
      href: "/calculator/furniture-appliances",
    },
    {
      category: "Heavy Equipment",
      description: "Tractors, trailers, and construction equipment calculators",
      icon: Truck,
      href: "/calculator/heavy-equipment",
    },
    {
      category: "Business Vehicles",
      description: "IRS auto depreciation and business vehicle calculators",
      icon: Truck,
      href: "/calculator/business-vehicles",
    },
  ]

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Accurate Depreciation & Amortization Tools
              <br />
              <span className="text-blue-600">for Tax & Accounting</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Free, professional-grade calculators for Straight Line, Double Declining
              Balance, and MACRS methods. Maximize your tax deductions and simplify IRS
              compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/calculators"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                View All Calculators
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/glossary"
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tools Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Most Popular Depreciation Calculators
          </h2>
          <p className="text-lg text-gray-600">
            Choose the right depreciation method for your assets
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredTools.map((tool) => {
            const Icon = tool.icon
            return (
              <Link
                key={tool.title}
                to={tool.href}
                className="group bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`${tool.color} rounded-lg p-3 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {tool.title}
                    </h3>
                    <p className="text-sm text-gray-600">{tool.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Asset Specific Tools */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Asset Specific Tools
            </h2>
            <p className="text-lg text-gray-600">
              Specialized calculators for different asset categories
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {assetTools.map((tool) => {
              const Icon = tool.icon
              return (
                <Link
                  key={tool.category}
                  to={tool.href}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all group"
                >
                  <Icon className="w-8 h-8 text-blue-600 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {tool.category}
                  </h3>
                  <p className="text-sm text-gray-600">{tool.description}</p>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-blue-600 rounded-2xl p-8 sm:p-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Why Use Depreciation Hub?
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Our tools help you maximize tax deductions, simplify IRS compliance (Form
              4562), and master accounting fundamentals with zero cost.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-blue-500 bg-opacity-30 rounded-lg p-6">
                <div className="text-3xl font-bold text-white mb-2">100%</div>
                <div className="text-sm text-blue-100">Free Forever</div>
              </div>
              <div className="bg-blue-500 bg-opacity-30 rounded-lg p-6">
                <div className="text-3xl font-bold text-white mb-2">IRS</div>
                <div className="text-sm text-blue-100">Compliant Methods</div>
              </div>
              <div className="bg-blue-500 bg-opacity-30 rounded-lg p-6">
                <div className="text-3xl font-bold text-white mb-2">Pro</div>
                <div className="text-sm text-blue-100">Grade Accuracy</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default Home
